import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentJointSuitabilityData,
  type EnrollmentJointSuitabilitySubmitInput,
  type EnrollmentJointSuitabilitySubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment Joint Suitability data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointSuitabilityData>
 * @example const response = await getEnrollmentJointSuitabilityData()
 */
export const getEnrollmentJointSuitabilityData = async (
  context?: DefaultContext,
): Promise<EnrollmentJointSuitabilityData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentJointSuitabilityData',
      query: `mutation getEnrollmentJointSuitabilityData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          errors
          isError
          activePerson {
            applicationId
            path
            progress
            name {
              firstName
            }
            isBrokerDealerEmployer
            isDirectorPolicyMaker
            isPoliticallyExposedPerson
            affiliatedPoliticalOrganizations
            hasPoliticallyExposedImmediateFamilyMembers
            politicallyExposedImmediateFamilyMembers
            directorPolicyMakerOfCompany
          }
        }
      }`,
    },
    {
      input: {},
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointSuitabilityData }
  return response.submitJointEnrollment
}

export const useGetEnrollmentJointSuitabilityData = createQueryHook<undefined, EnrollmentJointSuitabilityData>(
  getEnrollmentJointSuitabilityData,
)

/**
 * Submit Enrollment Joint Suitability data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointSuitabilitySubmitOutput>
 * @example const response = await submitEnrollmentJointSuitabilityData();
 */
export const submitEnrollmentJointSuitabilityData = async (
  enrollmentSuitabilitySubmitInput: EnrollmentJointSuitabilitySubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentJointSuitabilitySubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentJointSuitabilityData',
      query: `mutation submitEnrollmentJointSuitabilityData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          errors
          isError
          activePerson {
            path
            missingField
          }
        }
      }`,
    },
    {
      input: enrollmentSuitabilitySubmitInput,
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointSuitabilitySubmitOutput }
  return response.submitJointEnrollment
}

export const useSubmitEnrollmentJointSuitabilityData = createMutationHook<
  EnrollmentJointSuitabilitySubmitInput,
  EnrollmentJointSuitabilitySubmitOutput
>(submitEnrollmentJointSuitabilityData)
